import type { ApolloError } from '@apollo/client';
import { PageError } from '@cosuno/cosuno-ui';
import * as Sentry from '@sentry/browser';
import type { GraphQLFormattedError } from 'graphql';
import React, { useContext, useEffect } from 'react';

import { AUTHENTICATION_ERROR_TYPES } from './constants';
import { AuthContext } from './contexts/authContext';
import type { AuthLocationState } from './types';

interface Props {
  error: ApolloError;
  loginState?: AuthLocationState;
}

const isUnauthenticatedError = (error: GraphQLFormattedError) =>
  error.extensions?.code === AUTHENTICATION_ERROR_TYPES.unauthenticated ||
  error.message === 'Access denied! You need to be authorized to perform this action!';
const isCommonNetworkError = (error: Error) => error.message === 'Failed to fetch';

export const BaseQueryErrors: React.FC<Props> = ({
  error: { graphQLErrors, networkError },
  loginState,
}) => {
  const { logout, logoutOnUnauthenticated } = useContext(AuthContext);

  useEffect(() => {
    if (networkError && !isCommonNetworkError(networkError)) {
      Sentry.captureException(networkError);
    }
  }, [networkError]);

  // disable logout on Unauthenticated errors as it might cause
  // unwanted side effects in e2e tests
  // https://github.com/cosuno/cosuno/pull/11461
  if (graphQLErrors.some(isUnauthenticatedError) && logoutOnUnauthenticated) {
    logout({ redirectBack: true, loginState });
    return null;
  }

  return (
    <PageError
      message={[
        ...graphQLErrors.map(({ message }) => message),
        ...(networkError ? [networkError.message] : []),
      ]}
    />
  );
};
